import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useToast } from '@/contexts/admin/ToastContext';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { confirmationStrings, toastStrings } from '@/constants/residents/marketplaceStrings';
import { 
  MyListing,
  getConditionDisplayText, 
  getPickupOptionDisplayText,
  myListingToFormData,
} from '@/types/residents/marketplaceListing';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  PhotoGalleryModal,
  ConfirmationModal,
  PostItemModal,
  ImageCarousel,
  StatItem,
  SoldFeedbackModal,
  ListingConversationsList,
} from '@/components/residents/marketplace';
import { ListingBuyer } from '@/types/residents/marketplaceListing';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
  calculateCarouselDimensions,
} from '@/utils/marketplace';
import {
  useMarkAsSoldMutation,
  useDeleteListingMutation,
  useRepostListingMutation,
  useLazyGetMyListingsQuery,
  useLazyGetListingBuyersQuery,
  useCreateFeedbackRequestMutation,
} from '@/store/api';

type MyListingItemDetailNavigationProp = StackNavigationProp<RootStackParamList, 'MyListingItemDetail'>;
type MyListingItemDetailRouteProp = RouteProp<RootStackParamList, 'MyListingItemDetail'>;

const PencilIcon = SVG_COMPONENTS.PENCIL;
const TrashIcon = SVG_COMPONENTS.TRASH;
const DeliveryIcon = RESIDENTS_SVG_COMPONENTS.DELIVERY;

export const MyListingItemDetailScreen: React.FC = () => {
  const navigation = useNavigation<MyListingItemDetailNavigationProp>();
  const route = useRoute<MyListingItemDetailRouteProp>();
  const { layoutMode, width: screenWidth, isWeb } = useResponsive();
  const { showSuccess, showError } = useToast();

  // API mutations and queries
  const [markAsSold, { isLoading: isMarkingSold }] = useMarkAsSoldMutation();
  const [deleteListing, { isLoading: isDeleting }] = useDeleteListingMutation();
  const [repostListing, { isLoading: isReposting }] = useRepostListingMutation();
  const [getMyListings] = useLazyGetMyListingsQuery();
  const [getListingBuyers] = useLazyGetListingBuyersQuery();
  const [createFeedbackRequest] = useCreateFeedbackRequestMutation();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Calculate carousel dimensions based on layout
  const carouselDimensions = calculateCarouselDimensions(layoutMode, screenWidth);
  
  const { listing } = route.params;
  const [currentListing, setCurrentListing] = useState<MyListing>(listing);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isMarkSoldModalVisible, setIsMarkSoldModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [listingBuyers, setListingBuyers] = useState<ListingBuyer[]>([]);

  // Fetch fresh listing data on mount to get accurate views/saved counts
  useEffect(() => {
    const fetchFreshListing = async () => {
      try {
        const result = await getMyListings({ status: 'all' }).unwrap();
        const freshListing = result.find(l => l.id === listing.id);
        if (freshListing) {
          setCurrentListing(freshListing);
        }
      } catch (error) {
        console.warn('Failed to fetch fresh listing data:', error);
      }
    };
    fetchFreshListing();
  }, [listing.id, getMyListings]);

  const isActive = currentListing.status === 'active';

  // Format sold date
  const formatSoldDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleImagePress = (index: number) => {
    setGalleryInitialIndex(index);
    setIsGalleryVisible(true);
  };

  const handleCloseGallery = () => {
    setIsGalleryVisible(false);
  };

  const handleEditPress = () => {
    setIsEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
  };

  const handleEditSuccess = async () => {
    setIsEditModalVisible(false);
    showSuccess(toastStrings.listingUpdated);
    
    // Refetch listing data from API to show updated content
    try {
      const result = await getMyListings({ status: 'all' }).unwrap();
      const updatedListing = result.find(l => l.id === currentListing.id);
      if (updatedListing) {
        setCurrentListing(updatedListing);
      }
    } catch (error) {
      // Silently fail - user can navigate back and forth to see fresh data
      console.warn('Failed to refetch listing after edit:', error);
    }
  };

  const handleMarkSoldPress = () => {
    setIsMarkSoldModalVisible(true);
  };

  const handleMarkSoldCancel = () => {
    setIsMarkSoldModalVisible(false);
  };

  const handleMarkSoldProceed = async () => {
    try {
      await markAsSold(currentListing.id).unwrap();
      setIsMarkSoldModalVisible(false);
      
      // Check if there are buyers who messaged about this listing
      try {
        const buyers = await getListingBuyers(currentListing.id).unwrap();
        if (buyers && buyers.length > 0) {
          // Show feedback modal to select buyer
          setListingBuyers(buyers);
          setIsFeedbackModalVisible(true);
        } else {
          // No buyers, navigate directly to profile with success toast
          showSuccess(toastStrings.itemMarkedSold);
          navigation.navigate('MarketplaceProfile', { 
            action: 'sold', 
            listingId: currentListing.id 
          });
        }
      } catch (buyersError) {
        // If fetching buyers fails, just navigate with success toast
        console.warn('Failed to fetch listing buyers:', buyersError);
        showSuccess(toastStrings.itemMarkedSold);
        navigation.navigate('MarketplaceProfile', { 
          action: 'sold', 
          listingId: currentListing.id 
        });
      }
    } catch (error) {
      setIsMarkSoldModalVisible(false);
      showError(toastStrings.markSoldFailed);
    }
  };

  const handleFeedbackModalClose = () => {
    setIsFeedbackModalVisible(false);
    setListingBuyers([]);
    showSuccess(toastStrings.itemMarkedSold);
    navigation.navigate('MarketplaceProfile', { 
      action: 'sold', 
      listingId: currentListing.id 
    });
  };

  const handleFeedbackSubmit = async (buyerId: string | null) => {
    // Create feedback request if a buyer was selected
    if (buyerId) {
      try {
        await createFeedbackRequest({
          listingId: currentListing.id,
          buyerId,
        }).unwrap();
      } catch (error) {
        // Log error but don't block navigation - feedback request is optional
        console.warn('Failed to create feedback request:', error);
      }
    }
    
    setIsFeedbackModalVisible(false);
    setListingBuyers([]);
    showSuccess(toastStrings.itemMarkedSold);
    navigation.navigate('MarketplaceProfile', { 
      action: 'sold', 
      listingId: currentListing.id 
    });
  };

  const handlePostAgainPress = async () => {
    try {
      await repostListing(currentListing.id).unwrap();
      showSuccess(toastStrings.itemPostedAgain);
      // Navigate back to profile with action to repost the listing
      navigation.navigate('MarketplaceProfile', { 
        action: 'reposted', 
        listingId: currentListing.id 
      });
    } catch (error) {
      showError(toastStrings.repostFailed);
    }
  };

  const handleRemovePress = () => {
    setIsRemoveModalVisible(true);
  };

  const handleRemoveCancel = () => {
    setIsRemoveModalVisible(false);
  };

  const handleRemoveProceed = async () => {
    try {
      await deleteListing(currentListing.id).unwrap();
      setIsRemoveModalVisible(false);
      showSuccess(toastStrings.itemRemoved);
      // Navigate back to profile with action to remove the listing
      navigation.navigate('MarketplaceProfile', { 
        action: 'removed', 
        listingId: currentListing.id 
      });
    } catch (error) {
      setIsRemoveModalVisible(false);
      showError(toastStrings.removeFailed);
    }
  };

  return (
    <>
      <MarketplaceScreenLayout navigatorTitle="Item details">
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={[
            styles.content,
            { padding: contentPadding },
            !isMobile && styles.contentLarge,
          ]}
          showsVerticalScrollIndicator={false}
        >
          {/* Responsive content wrapper */}
          <View style={[
            styles.contentWrapper,
            maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
          ]}>
            {/* Back Navigation */}
            <BreadcrumbNavigation 
              onHomePress={handleGoToMarketplace}
              onBackPress={handleGoBack}
            />

            {/* Item Section */}
            <View style={styles.itemSection}>
              {/* Top Section */}
              <View style={styles.topSection}>
                {/* Image Carousel - centered on desktop */}
                <View style={!isMobile && styles.carouselContainer}>
                  <ImageCarousel
                    images={currentListing.images.map(img => img.url)}
                    onImagePress={handleImagePress}
                    width={!isMobile ? carouselDimensions.width : undefined}
                    height={!isMobile ? carouselDimensions.height : undefined}
                    layoutMode={layoutMode}
                    showArrows={!isMobile}
                  />
                </View>

                {/* Stats and Actions Row */}
                <View style={styles.statsActionsRow}>
                  {/* Stats (Active) or Sold Info */}
                  {isActive ? (
                    <View style={styles.statsContainer}>
                      <StatItem value={currentListing.views} label="Views" />
                      <StatItem value={currentListing.saved} label="Saved" />
                    </View>
                  ) : (
                    <View style={styles.soldInfoContainer}>
                      <Text style={styles.soldLabel}>Sold</Text>
                      <Text style={styles.soldDate}>{formatSoldDate(currentListing.soldAt)}</Text>
                    </View>
                  )}

                  {/* Action Buttons */}
                  <View style={styles.actionsContainer}>
                    {/* Edit Button */}
                    <TouchableOpacity
                      style={styles.editButton}
                      onPress={handleEditPress}
                      activeOpacity={0.8}
                    >
                      <PencilIcon width={19} height={19} color={MARKETPLACE_COLORS.accentBlueSolid} />
                    </TouchableOpacity>

                    {/* Primary Action Button */}
                    {isActive ? (
                      <TouchableOpacity
                        style={[styles.primaryActionButton, isMarkingSold && styles.primaryActionButtonDisabled]}
                        onPress={handleMarkSoldPress}
                        activeOpacity={0.8}
                        disabled={isMarkingSold}
                      >
                        {isMarkingSold ? (
                          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
                        ) : (
                          <Text style={styles.primaryActionButtonText}>Mark as sold</Text>
                        )}
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={[styles.primaryActionButton, isReposting && styles.primaryActionButtonDisabled]}
                        onPress={handlePostAgainPress}
                        activeOpacity={0.8}
                        disabled={isReposting}
                      >
                        {isReposting ? (
                          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
                        ) : (
                          <Text style={styles.primaryActionButtonText}>Post again</Text>
                        )}
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              </View>

              {/* Details Section */}
              <View style={styles.detailsSection}>
                {/* Price and Condition */}
                <View style={styles.priceConditionRow}>
                  <Text style={styles.price}>{currentListing.price}</Text>
                  <Text style={styles.condition}>{getConditionDisplayText(currentListing.condition)}</Text>
                </View>

                {/* Title */}
                <Text style={styles.title}>{currentListing.title}</Text>

                {/* Description */}
                <Text style={styles.description}>{currentListing.description}</Text>

                {/* Pickup Option Badge */}
                <View style={styles.pickupBadge}>
                  <DeliveryIcon width={16} height={16} color={MARKETPLACE_COLORS.textSecondary} />
                  <Text style={styles.pickupText}>{getPickupOptionDisplayText(currentListing.pickupOption)}</Text>
                </View>
              </View>

              {/* Buyer Conversations - show if there are any conversations for this listing */}
              {isActive && (
                <ListingConversationsList listingId={currentListing.id} />
              )}

              {/* Remove Listing */}
              <TouchableOpacity
                style={[styles.removeButton, isDeleting && styles.removeButtonDisabled]}
                onPress={handleRemovePress}
                activeOpacity={0.8}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color={MARKETPLACE_COLORS.dangerRed} />
                ) : (
                  <>
                    <TrashIcon width={20} height={20} color={MARKETPLACE_COLORS.dangerRed} />
                    <Text style={styles.removeButtonText}>Remove listing</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </MarketplaceScreenLayout>

      {/* Modals - rendered outside the layout */}
      <PhotoGalleryModal
        visible={isGalleryVisible}
        onClose={handleCloseGallery}
        images={currentListing.images.map(img => img.url)}
        initialIndex={galleryInitialIndex}
        onContactSeller={() => {}}
        onReport={() => {}}
      />
      <PostItemModal
        visible={isEditModalVisible}
        onClose={handleCloseEditModal}
        onSubmitSuccess={handleEditSuccess}
        initialData={myListingToFormData(currentListing)}
        isEditMode={true}
        listingId={currentListing.id}
      />
      <ConfirmationModal
        visible={isMarkSoldModalVisible}
        message={confirmationStrings.markAsSoldConfirmMessage}
        onCancel={handleMarkSoldCancel}
        onProceed={handleMarkSoldProceed}
      />
      <ConfirmationModal
        visible={isRemoveModalVisible}
        message={confirmationStrings.removeListingMessage}
        onCancel={handleRemoveCancel}
        onProceed={handleRemoveProceed}
      />
      <SoldFeedbackModal
        visible={isFeedbackModalVisible}
        buyers={listingBuyers}
        onClose={handleFeedbackModalClose}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 24,
  },
  contentLarge: {
    gap: 32,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 24,
  },
  // Item Section
  itemSection: {
    gap: 24,
  },
  topSection: {
    gap: 20,
  },
  carouselContainer: {
    alignItems: 'center',
  },
  // Stats and Actions Row
  statsActionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
  },
  statsContainer: {
    flexDirection: 'row',
    gap: 20,
  },
  soldInfoContainer: {
    alignItems: 'flex-start',
  },
  soldLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 21,
  },
  soldDate: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
  },
  editButton: {
    width: 34,
    height: 34,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryActionButton: {
    height: 34,
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryActionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 18,
  },
  primaryActionButtonDisabled: {
    opacity: 0.6,
  },
  // Details Section
  detailsSection: {
    gap: 16,
  },
  priceConditionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  price: {
    fontSize: 24,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 36,
  },
  condition: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
    textAlign: 'right',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  description: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 21,
  },
  pickupBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    borderRadius: 20,
    paddingHorizontal: 12,
    height: 28,
    alignSelf: 'flex-start',
  },
  pickupText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  // Remove Button
  removeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 20,
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.dividerGray,
  },
  removeButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.dangerRed,
    lineHeight: 21,
  },
  removeButtonDisabled: {
    opacity: 0.6,
  },
});
